import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import {
  useCreateChat,
  useDeleteChat,
  useGetChats,
  useGetMessagesByChatId,
  streamMessageToChat,
  resumeStreamChat,
  uploadSessionFile,
  listSessionFiles,
  deleteSessionFile
} from '../../services/agent.service';
import { useUserGroupsCurrentUserAdminState } from '../../services/user.service';
import { QUERY_KEYS } from '../../services/constants';
import { reportError } from '../../services/logger';

const AUTHOR_TYPES = { AI: 'AI', USER: 'User' };
// Must match _ACTION_REFRESH_SENTINEL in service.py
const ACTION_REFRESH_SENTINEL = '<!--datox-refresh-->';

export default function useAgentPanel() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [activeChatId, setActiveChatId] = useState(null);
  const [isNewChat, setIsNewChat] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  // Non-null while the agent is waiting for the user to confirm/deny an action.
  // Shape: { chatId, tempId } — tempId points to the AI bubble to attach buttons to.
  const [pendingInterrupt, setPendingInterrupt] = useState(null);
  // Per-session attached files: [{filename, char_count}].
  // pendingFiles holds File objects before a chat exists; once a chat is
  // created they are uploaded and moved into sessionFiles.
  const [sessionFiles, setSessionFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const queryClient = useQueryClient();
  const abortRef = useRef(null);
  // Tracks whether the current chat has local streaming state that must not be
  // overwritten by a server refetch triggered from onDone/invalidateQueries.
  // Reset whenever the user navigates to a different chat.
  const localSessionRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: currentUser, isLoading: isLoadingCurrentUser } =
    useUserGroupsCurrentUserAdminState();

  const { data: chats = [], isLoading: isLoadingChats } = useGetChats({
    searchQuery: debouncedSearchQuery,
    queryOptions: { enabled: !isLoadingCurrentUser && currentUser?.role !== 'Approver' }
  });

  const { data: activeChatMessages = [], isLoading: isLoadingMessages } = useGetMessagesByChatId({
    chatId: activeChatId,
    queryOptions: { enabled: !!activeChatId && !isNewChat }
  });

  const createChatMutation = useCreateChat({
    mutationOptions: {
      onSuccess: async (newChat, variables) => {
        queryClient.invalidateQueries([QUERY_KEYS.GET_AGENT_CHATS]);
        setActiveChatId(newChat.id);
        setIsNewChat(false);
        // Upload any files attached before the chat existed
        if (pendingFiles.length > 0) {
          const uploaded = await Promise.allSettled(
            pendingFiles.map(f => uploadSessionFile(newChat.id, f))
          );
          const succeeded = uploaded.filter(r => r.status === 'fulfilled').map(r => r.value);
          setSessionFiles(succeeded);
          setPendingFiles([]);
        }
        _streamMessage(newChat.id, variables.content);
      }
    }
  });

  const _streamMessage = useCallback(
    (chatId, content) => {
      const tempId = `streaming-${Date.now()}`;
      const controller = new AbortController();
      abortRef.current = controller;
      setStreamingMessageId(tempId);
      setIsStreaming(true);
      localSessionRef.current = true; // local state is now the source of truth
      let accumulatedContent = '';

      // Add a placeholder AI message that will fill progressively
      setMessages(prev => [
        ...prev,
        {
          id: tempId,
          content: '',
          author_type: AUTHOR_TYPES.AI,
          created_at: new Date(),
          isStreaming: true,
          steps: []
        }
      ]);

      const onProgress = ({ tool, message }) => {
        setMessages(prev =>
          prev.map(m => {
            if (m.id !== tempId) return m;
            const steps = m.steps ? m.steps.map(s => ({ ...s, done: true })) : [];
            return { ...m, steps: [...steps, { tool, message, done: false }] };
          })
        );
      };

      streamMessageToChat({
        chatId,
        data: { content },
        signal: controller.signal,
        onProgress,
        onChunk: chunk => {
          accumulatedContent += chunk;
          // Strip the sentinel from the displayed content — it must never be shown to the user
          const displayChunk = chunk.replace(ACTION_REFRESH_SENTINEL, '');
          if (displayChunk) {
            setMessages(prev =>
              prev.map(m => (m.id === tempId ? { ...m, content: m.content + displayChunk } : m))
            );
          }
        },
        onFinalAnswer: finalContent => {
          // The backend emits the authoritative answer after all tool calls resolve.
          // This replaces any streamed LLM tokens with the correct verbatim content
          // (e.g. list_agent_capabilities with datox-action: links) and also carries
          // the _ACTION_REFRESH_SENTINEL so data invalidation triggers correctly.
          accumulatedContent = finalContent;
          const displayContent = finalContent.replace(ACTION_REFRESH_SENTINEL, '').trimEnd();
          setMessages(prev =>
            prev.map(m => (m.id === tempId ? { ...m, content: displayContent } : m))
          );
        },
        onInterrupt: payload => {
          // Graph paused — show the confirmation question and stop streaming indicator.
          const question = payload?.message ?? 'Do you want to proceed?';
          setMessages(prev =>
            prev.map(m =>
              m.id === tempId
                ? { ...m, content: question, isStreaming: false, isInterrupted: true }
                : m
            )
          );
          setIsStreaming(false);
          setStreamingMessageId(null);
          setPendingInterrupt({ chatId, tempId });
        },
        onDone: () => {
          setIsStreaming(false);
          setStreamingMessageId(null);

          // Detect and strip the refresh sentinel, then invalidate all queries if present
          const needsRefresh = accumulatedContent.includes(ACTION_REFRESH_SENTINEL);
          const cleanContent = accumulatedContent.replace(ACTION_REFRESH_SENTINEL, '').trimEnd();

          setMessages(prev =>
            prev.map(m =>
              m.id === tempId
                ? {
                    ...m,
                    isStreaming: false,
                    content: cleanContent,
                    steps: (m.steps || []).map(s => ({ ...s, done: true }))
                  }
                : m
            )
          );

          if (needsRefresh) {
            // Invalidate all cached queries so every visible page re-fetches its data
            queryClient.invalidateQueries();
          }

          // Invalidate to sync with server state
          queryClient.invalidateQueries([QUERY_KEYS.GET_AGENT_CHAT_MESSAGES, chatId]);
        },
        onError: err => {
          setIsStreaming(false);
          setStreamingMessageId(null);
          setMessages(prev =>
            prev.map(m =>
              m.id === tempId
                ? {
                    ...m,
                    content: `⚠️ ${err || 'Agent error. Please try again.'}`,
                    isStreaming: false,
                    isError: true
                  }
                : m
            )
          );
        }
      });
    },
    [queryClient]
  );

  // Called when the user clicks Yes/No on a confirmation bubble.
  const onConfirmInterrupt = useCallback(
    approved => {
      if (!pendingInterrupt) return;
      const { chatId, tempId } = pendingInterrupt;
      setPendingInterrupt(null);

      // Swap out the confirmation buttons for a streaming cursor again.
      setStreamingMessageId(tempId);
      setIsStreaming(true);
      let accumulatedContent = '';

      setMessages(prev =>
        prev.map(m =>
          m.id === tempId ? { ...m, content: '', isStreaming: true, isInterrupted: false } : m
        )
      );

      resumeStreamChat({
        chatId,
        approved,
        onChunk: chunk => {
          accumulatedContent += chunk;
          const displayChunk = chunk.replace(ACTION_REFRESH_SENTINEL, '');
          if (displayChunk) {
            setMessages(prev =>
              prev.map(m => (m.id === tempId ? { ...m, content: m.content + displayChunk } : m))
            );
          }
        },
        onFinalAnswer: finalContent => {
          accumulatedContent = finalContent;
          const displayContent = finalContent.replace(ACTION_REFRESH_SENTINEL, '').trimEnd();
          setMessages(prev =>
            prev.map(m => (m.id === tempId ? { ...m, content: displayContent } : m))
          );
        },
        onDone: () => {
          setIsStreaming(false);
          setStreamingMessageId(null);
          const needsRefresh = accumulatedContent.includes(ACTION_REFRESH_SENTINEL);
          const cleanContent = accumulatedContent.replace(ACTION_REFRESH_SENTINEL, '').trimEnd();
          setMessages(prev =>
            prev.map(m =>
              m.id === tempId ? { ...m, isStreaming: false, content: cleanContent } : m
            )
          );
          if (needsRefresh) queryClient.invalidateQueries();
          queryClient.invalidateQueries([QUERY_KEYS.GET_AGENT_CHAT_MESSAGES, chatId]);
        },
        onError: err => {
          setIsStreaming(false);
          setStreamingMessageId(null);
          setMessages(prev =>
            prev.map(m =>
              m.id === tempId
                ? {
                    ...m,
                    content: `⚠️ ${err || 'Agent error. Please try again.'}`,
                    isStreaming: false,
                    isError: true
                  }
                : m
            )
          );
        }
      });
    },
    [pendingInterrupt, queryClient]
  );

  const injectLocalMessages = useCallback((userText, aiContent) => {
    const now = new Date();
    setMessages(prev => [
      ...prev,
      { id: Date.now(), content: userText, author_type: AUTHOR_TYPES.USER, created_at: now },
      { id: Date.now() + 1, content: aiContent, author_type: AUTHOR_TYPES.AI, created_at: now }
    ]);
  }, []);

  const onStop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setStreamingMessageId(prev => {
      setMessages(msgs =>
        msgs.map(m =>
          m.id === prev
            ? { ...m, isStreaming: false, steps: (m.steps || []).map(s => ({ ...s, done: true })) }
            : m
        )
      );
      return null;
    });
  }, []);

  const onSendMessage = useCallback(
    (content = inputValue) => {
      const trimmed = content.trim();
      if (!trimmed || isStreaming || pendingInterrupt) return;

      // Append user message immediately
      setMessages(prev => [
        ...prev,
        { id: Date.now(), content: trimmed, author_type: AUTHOR_TYPES.USER, created_at: new Date() }
      ]);
      setInputValue('');

      if (activeChatId) {
        _streamMessage(activeChatId, trimmed);
      } else {
        createChatMutation.mutate({ content: trimmed });
      }
    },
    [inputValue, isStreaming, activeChatId, _streamMessage, createChatMutation]
  );

  const deleteChatMutation = useDeleteChat({
    mutationOptions: {
      onSuccess: (_, chatId) => {
        queryClient.invalidateQueries([QUERY_KEYS.GET_AGENT_CHATS]);
        if (activeChatId === chatId) {
          setActiveChatId(null);
          setMessages([]);
          setIsNewChat(true);
        }
      }
    }
  });

  const onDeleteChat = useCallback(
    chatId => {
      deleteChatMutation.mutate(chatId);
    },
    [deleteChatMutation]
  );

  const onNewChat = useCallback(() => {
    setActiveChatId(null);
    setMessages([]);
    setIsNewChat(true);
    setInputValue('');
    setSessionFiles([]);
    setPendingFiles([]);
    localSessionRef.current = false;
  }, []);

  const onSelectChat = useCallback(
    async chatId => {
      if (!chatId) {
        onNewChat();
        return;
      }
      localSessionRef.current = false; // switching chat — let server data take over
      setActiveChatId(chatId);
      setIsNewChat(false);
      const cached = queryClient.getQueryData([QUERY_KEYS.GET_AGENT_CHAT_MESSAGES, chatId]);
      setMessages(cached || []);
      // Load any session files already attached to this chat
      try {
        const files = await listSessionFiles(chatId);
        setSessionFiles(Array.isArray(files) ? files : []);
      } catch {
        setSessionFiles([]);
      }
    },
    [queryClient, onNewChat]
  );

  const attachSessionFile = useCallback(
    async file => {
      if (!activeChatId || isNewChat) {
        // No active chat yet — hold until chat is created
        setPendingFiles(prev => [...prev.filter(f => f.name !== file.name), file]);
        setSessionFiles(prev => [
          ...prev.filter(f => f.filename !== file.name),
          { filename: file.name, char_count: 0, pending: true }
        ]);
        return;
      }
      try {
        const result = await uploadSessionFile(activeChatId, file);
        setSessionFiles(prev => [...prev.filter(f => f.filename !== file.name), result]);
      } catch (err) {
        reportError('AgentPanel.sessionFileUpload', err);
      }
    },
    [activeChatId, isNewChat]
  );

  const removeSessionFile = useCallback(
    async filename => {
      setSessionFiles(prev => prev.filter(f => f.filename !== filename));
      setPendingFiles(prev => prev.filter(f => f.name !== filename));
      if (activeChatId && !isNewChat) {
        try {
          await deleteSessionFile(activeChatId, filename);
        } catch {
          // best-effort
        }
      }
    },
    [activeChatId, isNewChat]
  );

  // Sync messages when react-query loads history for a selected chat.
  // Skipped while a local streaming session is active to preserve message order.
  useEffect(() => {
    if (localSessionRef.current) return; // local state is source of truth during streaming
    if (activeChatId && activeChatMessages.length && !isNewChat) {
      const formatted = activeChatMessages
        .slice()
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .map(m => ({
          id: m.id,
          content:
            typeof m.content === 'string'
              ? m.content.replace(ACTION_REFRESH_SENTINEL, '').trimEnd()
              : m.content,
          author_type: m.author_type,
          created_at: new Date(m.created_at)
        }));
      setMessages(formatted);
    }
  }, [activeChatMessages, activeChatId, isNewChat]);

  return {
    messages,
    inputValue,
    activeChatId,
    chats,
    searchQuery,
    isLoadingChats,
    isLoadingMessages,
    isStreaming,
    streamingMessageId,
    pendingInterrupt,
    sessionFiles,
    setInputValue,
    onSendMessage,
    injectLocalMessages,
    onStop,
    onConfirmInterrupt,
    onNewChat,
    onSelectChat,
    onDeleteChat,
    attachSessionFile,
    removeSessionFile,
    onSearchChange: setSearchQuery
  };
}
